import React, { useState, useEffect } from 'react';
import { useQualityEvents } from '../../context/QualityEventsContext';
import { useApp } from '../../context/AppContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import TraceLabel from '../trace/TraceLabel';
import { useTrace } from '../../context/TraceContext';

const CAPACreateDialog = ({ open, onOpenChange, onCreated, deviationId = null, initialData = null }) => {
  const { createCAPA, deviations } = useQualityEvents();
  const { currentRole } = useApp();
  const { pidTraceMode } = useTrace();

  const [formData, setFormData] = useState({
    title: '',
    deviationId: deviationId || '',
    priority: 'Medium',
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    effectivenessCheck: '30 days',
    correctiveActions: [''],
    preventiveActions: ['']
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        deviationId: initialData.deviationId || deviationId || '',
        priority: initialData.priority || 'Medium',
        dueDate: initialData.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        effectivenessCheck: initialData.effectivenessCheck || '30 days',
        correctiveActions: initialData.correctiveActions || [''],
        preventiveActions: initialData.preventiveActions || ['']
      });
    }
  }, [initialData, deviationId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const newCAPA = createCAPA({
        title: formData.title,
        deviationId: formData.deviationId || null,
        priority: formData.priority,
        dueDate: formData.dueDate,
        effectivenessCheck: formData.effectivenessCheck,
        correctiveActions: formData.correctiveActions.filter(a => a.trim()),
        preventiveActions: formData.preventiveActions.filter(a => a.trim()),
        status: 'Open'
      });

      toast.success('CAPA created', {
        description: `Created ${newCAPA.id}`
      });

      onCreated?.(newCAPA);
      onOpenChange(false);
      
      // Reset form
      setFormData({
        title: '',
        deviationId: deviationId || '',
        priority: 'Medium',
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        effectivenessCheck: '30 days',
        correctiveActions: [''],
        preventiveActions: ['']
      });
    } catch (error) {
      toast.error('Failed to create CAPA', {
        description: error.message
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableDeviations = deviations.filter(d => d.status !== 'Closed' && !d.capaId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-[700px] max-h-[90vh] overflow-y-auto ${pidTraceMode ? 'border-cyan-300' : ''}`}>
        <TraceLabel prd="PRD §6.4" req="SM-CAPA-001" />
        <DialogHeader>
          <DialogTitle>New CAPA</DialogTitle>
          <DialogDescription>
            Create a new Corrective and Preventive Action. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">CAPA Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Describe the corrective/preventive action"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="deviationId">Linked Deviation (optional)</Label>
            <Select
              value={formData.deviationId || undefined}
              onValueChange={(value) => setFormData({ ...formData, deviationId: value || null })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select deviation" />
              </SelectTrigger>
              <SelectContent>
                {availableDeviations.map(dev => (
                  <SelectItem key={dev.id} value={dev.id}>
                    {dev.id} - {dev.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority">Priority *</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dueDate">Due Date *</Label>
              <Input
                id="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="effectivenessCheck">Effectiveness Check Period</Label>
            <Select
              value={formData.effectivenessCheck}
              onValueChange={(value) => setFormData({ ...formData, effectivenessCheck: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30 days">30 days</SelectItem>
                <SelectItem value="60 days">60 days</SelectItem>
                <SelectItem value="90 days">90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Corrective Actions</Label>
            {formData.correctiveActions.map((action, idx) => (
              <div key={idx} className="flex gap-2">
                <Textarea
                  value={action}
                  onChange={(e) => {
                    const newActions = [...formData.correctiveActions];
                    newActions[idx] = e.target.value;
                    setFormData({ ...formData, correctiveActions: newActions });
                  }}
                  placeholder="Describe corrective action"
                  rows={2}
                />
                {formData.correctiveActions.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newActions = formData.correctiveActions.filter((_, i) => i !== idx);
                      setFormData({ ...formData, correctiveActions: newActions });
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFormData({ ...formData, correctiveActions: [...formData.correctiveActions, ''] })}
            >
              Add Corrective Action
            </Button>
          </div>

          <div className="space-y-2">
            <Label>Preventive Actions</Label>
            {formData.preventiveActions.map((action, idx) => (
              <div key={idx} className="flex gap-2">
                <Textarea
                  value={action}
                  onChange={(e) => {
                    const newActions = [...formData.preventiveActions];
                    newActions[idx] = e.target.value;
                    setFormData({ ...formData, preventiveActions: newActions });
                  }}
                  placeholder="Describe preventive action"
                  rows={2}
                />
                {formData.preventiveActions.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newActions = formData.preventiveActions.filter((_, i) => i !== idx);
                      setFormData({ ...formData, preventiveActions: newActions });
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFormData({ ...formData, preventiveActions: [...formData.preventiveActions, ''] })}
            >
              Add Preventive Action
            </Button>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#fd4816] hover:bg-[#ea380c] text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create CAPA'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CAPACreateDialog;

